import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ClickAwayListener,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import CallIconDark from 'crono-fe-common/icons/Icon-Call-Dark';
import EmailIconDark from 'crono-fe-common/icons/Icon-Email-Dark';
import LinkedinIconInMailCompassDark from 'crono-fe-common/icons/Icon-Linkedin-Inmail-Only-Compass-Dark';
import { colors } from 'crono-fe-common/theme';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import { ConditionalSequenceStepWrapper } from './style';
import ReplyThreadIcon from 'crono-fe-common/icons/Icon-Reply-Thread';
import AddTemplateIconS from 'crono-fe-common/icons/Icon-Add-Template-S';
import SelectedTemplateIcon from 'crono-fe-common/icons/Icon-Selected-Template';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { Template } from 'crono-fe-common/types/template';
import { StrategyConditionalStep } from 'crono-fe-common/types/DTO/strategyConditionalStep';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import SkipIcon from 'crono-fe-common/icons/Icon-Skip';
import { ReactComponent as CloseIconDark } from 'assets/images/Icon_Close_Dark.svg';
import LinkedinIconInMailCompass from 'crono-fe-common/icons/Icon-Linkedin-Inmail-New';
import EditableTemplate from '../stepDetailComponent/editableTemplate';
import { PersonalizationIdentity } from 'hooks/strategy/usePersonalizationIdentity';
import { Switch } from '@crono-one/design-system';
import TimeOfDayPicker from 'components/TimeOfDayPicker';
import { CustomOptionsTooltip } from 'pages/strategy/customOptionsTooltip';
import { StepTypeOptionsWrapper } from '../stepDetailComponent/style';
import { ReactComponent as TopArrowIcon } from 'crono-fe-common/icons/Top-Arrow.svg';
import { ReactComponent as BottomArrowIcon } from 'crono-fe-common/icons/Bottom-Arrow.svg';
import { useAuth } from 'context/auth';
import { StrategyConditionalStepPatch } from 'crono-fe-common/types/DTO/strategyConditionalStepPatch';
import { cn } from 'utils/style-utils';
import GuidedTourSpotlight from 'components/GuidedTour';
import PersonalisedBadge from 'pages/taskBoard/sequenceInstance/sequenceInstanceTaskComponent/personalisedBadge';
import { IfInvitationIsNotAcceptedBanner } from 'pages/strategy/createStrategyWithAI/StrategyStructureEdit/strategyStructureStep/strategyStepBanners';
import IconStrategySettings from 'crono-fe-common/icons/Icon-Strategy-Settings';
import StrategySelectorModal from 'components/StrategySelectorModal';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import EditPencilIcon from 'crono-fe-common/icons/Icon-Edit-pencil';
import { Strategy } from 'crono-fe-common/types/strategy';
import ABTestingTemplates from '../abTestingTemplates';
import PlusIcon from 'crono-fe-common/icons/Icon-Plus';
import { getDateFromScheduleTime } from 'crono-fe-common/utils';
import type { HandleAllChangesStrategyStepTemplateType } from '../strategyDetailModal/strategyDetailModal';

type ReplyThread = 'newThread' | 'existingThread';

interface IProps {
  conditionalStep: StrategyConditionalStep;
  handleChange:
    | ((
        type: string,
        payload: boolean | Date | null | Template,
        index: number,
      ) => void)
    | null;
  index: number;

  openConditionalTemplatePicker: (
    type: SwitchToTask,
    abTesting?: boolean,
  ) => void;
  previousThreadSubject: string | undefined;
  onSuccessEditTemplate: (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => void;
  disableEditsButTemplate?: boolean;
  onSaveReturnTemplate?: (subject: string, content: string) => void;
  patchConditionalStep?: (data: StrategyConditionalStepPatch) => void;
  handleChangeAbTesting?: (
    type: HandleAllChangesStrategyStepTemplateType,
    payload: {
      templateIndex: number;
      enabled?: boolean;
      strategyStepTemplateId: number;
    },
    index: number,
  ) => void;
  step?: StrategyStep;

  //For template variables substitution
  isSequenceInstance?: boolean;
  prospectId?: string | null;
  accountId?: string | null;
  //Who the variables resolve as; defaults to the logged-in user
  personalization?: PersonalizationIdentity;
  previewOnly?: boolean;

  allowToPersonalize?: boolean;

  firstLinkedinMessageAfterInvitation?: boolean;
  stopAndJumpAlreadyPresent?: boolean;

  strategyId?: number;
}

interface SwitchTypeOption {
  type: SwitchToTask;
  icon: JSX.Element;
  text: string;
}
const ConditionalStepDetailComponent = ({
  conditionalStep,
  handleChange,
  index,
  openConditionalTemplatePicker,
  previousThreadSubject,
  patchConditionalStep,
  step,
  onSuccessEditTemplate,
  disableEditsButTemplate = false,
  onSaveReturnTemplate,
  isSequenceInstance,
  prospectId,
  accountId,
  personalization,
  previewOnly = false,
  allowToPersonalize,
  firstLinkedinMessageAfterInvitation = false,
  stopAndJumpAlreadyPresent = false,
  handleChangeAbTesting,
  strategyId,
}: IProps) => {
  const [isReplyThreadSubject, setIsReplyThreadSubject] = useState<ReplyThread>(
    conditionalStep.replyToThread ? 'existingThread' : 'newThread',
  );

  const handleTypeChange = (
    newConditionalType: SwitchToTask,
    jumpToStrategyId?: number,
  ) => {
    if (patchConditionalStep && step) {
      patchConditionalStep({
        strategyStepId: step.id,
        type: newConditionalType,
        newStrategyId: jumpToStrategyId ?? null,
        delay: newConditionalType === SwitchToTask.DELAY ? 1 : undefined,
        templateId: null,
        automatic: null,
        replyToThread: null,
        scheduleTime: null,
      });
    }
  };

  const isEmail = conditionalStep.type === SwitchToTask.TO_EMAIL;

  //The conditional template is shared by all the sequence instances, so it is
  //not applied to them: forwarding the new template id would record it as a
  //templateId patch on the parent step
  const handleSuccessEditConditionalTemplate = (
    data:
      | {
          newInsertedTemplateId: number;
          isAbTesting: boolean;
        }
      | undefined,
  ) => {
    onSuccessEditTemplate(data?.isAbTesting ? data : undefined);
  };

  const canManageAbTesting = !isSequenceInstance && !previewOnly;

  const emailSubject = useMemo(
    () =>
      isReplyThreadSubject === 'newThread'
        ? conditionalStep.template?.subject
        : previousThreadSubject,
    [isReplyThreadSubject, conditionalStep.template, previousThreadSubject],
  );

  const emailThreadButton = useMemo(
    () => (
      <div
        className={`flex-center email-thread ${
          disableEditsButTemplate ? 'disable-thread-selector' : ''
        }`}
      >
        <Select
          value={isReplyThreadSubject}
          startAdornment={
            <ReplyThreadIcon
              color={disableEditsButTemplate ? colors.grey11 : colors.mainDark}
            />
          }
          inputProps={{ IconComponent: () => null }}
          disabled={disableEditsButTemplate}
          defaultValue="newThread"
          onChange={(e) => {
            if (handleChange) {
              setIsReplyThreadSubject(e.target.value as ReplyThread);
              handleChange(
                'replyToThread',
                e.target.value === 'existingThread',
                index,
              );
            }
          }}
          sx={{
            fontSize: '14px',
            minWidth: '120px',
            boxShadow: 'none',
            fontWeight: '500',
            gap: '4px',
            padding: '0px',
            '.MuiOutlinedInput-input': {
              padding: '0px',
            },
            '.MuiSelect-select': {
              padding: '0px !important',
            },
            '.MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover': {
              color: colors.darkGreen,
              '&& svg path': {
                stroke: disableEditsButTemplate
                  ? colors.grey11
                  : colors.darkGreen,
              },

              '&& fieldset': {
                borderColor: 'transparent',
              },
            },
          }}
          MenuProps={{
            style: { zIndex: 3501 },
            PaperProps: {
              sx: {
                borderRadius: '8px',
                paddingInline: '6px',
                border: '1px solid #dadde9',
                boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
                '& .MuiMenuItem-root': {
                  fontWeight: '500',
                  fontSize: '12px',
                  lineHeight: '16px',
                  height: 'fit-content',
                  padding: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  borderRadius: '8px',
                  '&:hover': {
                    background: colors.primaryLight,
                    color: colors.primary,
                  },
                },
                '& .Mui-selected': {
                  color: colors.primary,
                  backgroundColor: 'transparent !important',
                },
              },
            },
          }}
        >
          <MenuItem value={'newThread'}>New email thread</MenuItem>
          <MenuItem value={'existingThread'}>Previous email thread</MenuItem>
        </Select>
      </div>
    ),
    [setIsReplyThreadSubject, handleChange, index, isReplyThreadSubject],
  );

  const renderBottomPart = useMemo(() => {
    //we hide other conditionals if stopAndJumpAlreadyPresent
    return (
      ((conditionalStep.type === SwitchToTask.TO_EMAIL ||
        conditionalStep.type === SwitchToTask.IN_MAIL ||
        conditionalStep.type === SwitchToTask.TO_CALL) &&
        !stopAndJumpAlreadyPresent) ||
      (conditionalStep.type ===
        SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY &&
        conditionalStep.newStrategyId &&
        conditionalStep.newStrategy)
    );
  }, [conditionalStep, stopAndJumpAlreadyPresent]);

  //#region StrategySelectorModal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const openStrategySelectorModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);
  const closeStrategySelectorModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);
  const setNewStrategyId = useCallback(
    (strategy: Strategy) => {
      handleTypeChange(
        SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY,
        strategy.id,
      );
      closeStrategySelectorModal();
    },
    [handleTypeChange, closeStrategySelectorModal],
  );
  //#endregion
  return (
    <>
      {isModalVisible && firstLinkedinMessageAfterInvitation && (
        <StrategySelectorModal
          close={closeStrategySelectorModal}
          returnSelectedStrategy={setNewStrategyId}
        />
      )}
      <ConditionalSequenceStepWrapper>
        <div
          className="step-row-conditional"
          id={
            conditionalStep
              ? `${conditionalStep.id.toString()}-conditional`
              : undefined
          }
        >
          <GuidedTourSpotlight
            guidedTourProperty="setDelayAndConditional"
            valueToSpotlightOn={'Second'}
            title="If Invitation is not accepted"
            subtitle="Manage conditions for each LinkedIn step. True always sends a message, while false can either skip or send an InMail."
            direction="top"
            style={{
              padding: 8,
              margin: -8,
              borderRadius: 12,
              width: '100%',
            }}
            disabled={!firstLinkedinMessageAfterInvitation || previewOnly}
          >
            <div className="step-content-single-step">
              <div
                className={cn('step-content-top', {
                  'step-content-no-border-bottom': renderBottomPart,
                })}
                style={{
                  borderBottom:
                    conditionalStep.template && !stopAndJumpAlreadyPresent
                      ? 'none'
                      : '',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: 'fit-content',
                }}
              >
                <IfInvitationIsNotAcceptedBanner
                  style={{
                    margin: 0,
                    width: '100%',
                  }}
                />
                <div
                  className="flex-center step-left-side-wrapper"
                  style={{
                    minWidth:
                      conditionalStep.type !== SwitchToTask.TO_EMAIL &&
                      conditionalStep.type !== SwitchToTask.IN_MAIL
                        ? '340px'
                        : undefined,
                    width: '100%',
                  }}
                >
                  <RenderConditionalStepType
                    step={conditionalStep}
                    handleTypeChange={handleTypeChange}
                    firstLinkedinMessageAfterInvitation={
                      firstLinkedinMessageAfterInvitation
                    }
                    isSequenceInstance={isSequenceInstance ?? false}
                    previewOnly={previewOnly}
                    openStrategySelectorModal={openStrategySelectorModal}
                    stopAndJumpAlreadyPresent={stopAndJumpAlreadyPresent}
                  />
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {(conditionalStep.type === SwitchToTask.TO_EMAIL ||
                      conditionalStep.type === SwitchToTask.IN_MAIL ||
                      conditionalStep.type === SwitchToTask.TO_CALL) &&
                    !previewOnly &&
                    !stopAndJumpAlreadyPresent ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {(conditionalStep.type === SwitchToTask.TO_EMAIL ||
                          conditionalStep.type === SwitchToTask.IN_MAIL) && (
                          <Tooltip
                            arrow
                            enterDelay={400}
                            enterNextDelay={400}
                            title={
                              disableEditsButTemplate ? (
                                <span
                                  style={{
                                    display: 'flex',
                                    textAlign: 'center',
                                  }}
                                >
                                  The change can’t be made for each contact.
                                  Please change this for the whole sequence
                                </span>
                              ) : conditionalStep.template ||
                                (conditionalStep.strategyStepTemplates &&
                                  conditionalStep.strategyStepTemplates.length >
                                    0) ? (
                                'Automate this step'
                              ) : (
                                'Toggle Auto. Can be turned on and off. Cannot be turned on if no template is selected'
                              )
                            }
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: disableEditsButTemplate
                                  ? 'not-allowed'
                                  : 'pointer',
                              }}
                            >
                              <Typography
                                fontSize={14}
                                fontWeight={500}
                                color={colors.grey11}
                              >
                                Auto
                              </Typography>

                              <Switch
                                checked={conditionalStep.automatic ?? false}
                                onCheckedChange={(checked) => {
                                  if (handleChange) {
                                    handleChange('automatic', checked, index);
                                  }
                                }}
                                disabled={
                                  !(
                                    conditionalStep.template ||
                                    (conditionalStep.strategyStepTemplates &&
                                      conditionalStep.strategyStepTemplates
                                        .length > 0)
                                  ) || disableEditsButTemplate
                                }
                                className="mx-2"
                              />
                            </div>
                          </Tooltip>
                        )}

                        <TimeOfDayPicker
                          value={getDateFromScheduleTime(
                            conditionalStep.scheduleTime ?? null,
                          )}
                          disabled={disableEditsButTemplate}
                          onChange={(newValue) => {
                            if (handleChange) {
                              handleChange('scheduleTime', newValue, index);
                            }
                          }}
                          className="min-w-24 max-w-24 w-24"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              {renderBottomPart ? (
                <div
                  className={cn('step-content-bottom', {
                    'step-content-no-border-top': renderBottomPart,
                  })}
                >
                  <div className="vertical-divider" />

                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        justifyContent: 'space-between',
                        flex: 1,
                        alignSelf: conditionalStep.template
                          ? 'flex-start'
                          : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                          paddingInline: '12px',
                        }}
                      >
                        <Tooltip
                          title={
                            disableEditsButTemplate ? (
                              <span
                                style={{ display: 'flex', textAlign: 'center' }}
                              >
                                The change can’t be made for each contact.
                                Please change this for the whole sequence
                              </span>
                            ) : null
                          }
                          enterDelay={400}
                          enterNextDelay={400}
                          arrow
                        >
                          <div
                            className={`flex-center ${
                              disableEditsButTemplate
                                ? 'step-component-template-disable'
                                : conditionalStep.template
                                  ? 'selected-template'
                                  : !conditionalStep.newStrategyId
                                    ? 'add-template'
                                    : ''
                            }`}
                          >
                            {(conditionalStep.strategyStepTemplates?.length ??
                            0 > 0) ? null : conditionalStep.template ? (
                              <>
                                <span className="flex-center icon-wrapper">
                                  <SelectedTemplateIcon
                                    className={'step-component-button'}
                                    color={colors.grey11}
                                    onClick={() => {
                                      if (!disableEditsButTemplate) {
                                        openConditionalTemplatePicker(
                                          conditionalStep.type,
                                        );
                                      }
                                    }}
                                  />
                                </span>

                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  noWrap
                                  onClick={() => {
                                    if (previewOnly) return;

                                    if (!disableEditsButTemplate) {
                                      openConditionalTemplatePicker(
                                        conditionalStep.type,
                                      );
                                    }
                                  }}
                                >
                                  {conditionalStep.template.title}
                                </Typography>

                                <span
                                  onClick={() => {
                                    if (
                                      handleChange &&
                                      !disableEditsButTemplate
                                    ) {
                                      handleChange(
                                        'template',
                                        { id: 0 } as Template,
                                        index,
                                      );
                                    }
                                  }}
                                  style={
                                    !previewOnly
                                      ? { cursor: 'pointer', display: 'flex' }
                                      : { display: 'none' }
                                  }
                                  className="close-template-option"
                                >
                                  <CloseMIcon color={colors.grey11} />
                                </span>
                              </>
                            ) : (
                              !conditionalStep.newStrategyId && (
                                <>
                                  <span className="flex-center icon-wrapper">
                                    <AddTemplateIconS
                                      className={'step-component-button'}
                                      onClick={() => {
                                        if (!disableEditsButTemplate) {
                                          openConditionalTemplatePicker(
                                            conditionalStep.type,
                                          );
                                        }
                                      }}
                                    />
                                  </span>

                                  <Typography
                                    fontSize={14}
                                    fontWeight={500}
                                    noWrap
                                    onClick={() => {
                                      if (!disableEditsButTemplate) {
                                        openConditionalTemplatePicker(
                                          conditionalStep.type,
                                        );
                                      }
                                    }}
                                  >
                                    Add template
                                  </Typography>
                                </>
                              )
                            )}
                            {conditionalStep.newStrategyId &&
                              conditionalStep.newStrategy?.name && (
                                <FlexDiv
                                  justifyContent={'start'}
                                  width={'100%'}
                                >
                                  <Typography
                                    color={colors.grey11}
                                    fontWeight={500}
                                    fontSize={14}
                                    component={'span'}
                                  >
                                    Sequence:{' '}
                                  </Typography>
                                  <div
                                    className={'change-conditional-strategy'}
                                    onClick={openStrategySelectorModal}
                                  >
                                    <Typography
                                      fontWeight={500}
                                      fontSize={14}
                                      component={'span'}
                                    >
                                      {conditionalStep.newStrategy.name}
                                    </Typography>
                                    <EditPencilIcon
                                      width={'18px'}
                                      height={'18px'}
                                      style={{ marginTop: '-3px' }}
                                    />
                                  </div>
                                </FlexDiv>
                              )}
                          </div>
                        </Tooltip>

                        {!!conditionalStep.template && (
                          <PersonalisedBadge
                            template={conditionalStep.template}
                          />
                        )}
                      </div>
                    </div>

                    {/* templates body if existing template */}
                    {conditionalStep.strategyStepTemplates &&
                    conditionalStep.strategyStepTemplates.length > 0 ? (
                      <>
                        {conditionalStep.strategyStepTemplates?.map(
                          (strategyStepTemplate, _index) => {
                            return (
                              <ABTestingTemplates
                                //This is a trick to force the re-render of the component when the template is changed
                                key={`${strategyStepTemplate.templateId}${strategyStepTemplate.template?.content.length}${_index}${strategyStepTemplate.template?.title}`}
                                strategyStepTemplate={strategyStepTemplate}
                                stepOrConditionalStep={conditionalStep}
                                stepId={conditionalStep.strategyStepId}
                                stepConditionalId={conditionalStep.id}
                                index={index}
                                currentIndex={_index}
                                handleChange={(type, payload, index) => {
                                  if (handleChangeAbTesting) {
                                    handleChangeAbTesting(type, payload, index);
                                  }
                                }}
                                strategyStepTemplates={
                                  conditionalStep.strategyStepTemplates!
                                }
                                onSuccessEditTemplate={onSuccessEditTemplate}
                                EmailThreadButton={emailThreadButton}
                                previewOnly={previewOnly}
                              />
                            );
                          },
                        )}
                        {canManageAbTesting && (
                          <>
                            <div className="horizontal-divider" />

                            <div
                              onClick={() => {
                                openConditionalTemplatePicker(
                                  conditionalStep.type,
                                  true,
                                );
                              }}
                              className={'ab-testing-wrapper'}
                            >
                              <PlusIcon />

                              <Typography fontSize={'14px'} fontWeight={500}>
                                Add template
                              </Typography>
                            </div>
                          </>
                        )}
                      </>
                    ) : conditionalStep?.template ? (
                      <>
                        <EditableTemplate
                          allowToPersonalize={allowToPersonalize}
                          id={'editable-template-conditional-right-leaf'}
                          applyIdOnlyOnEditMode={true}
                          previewOnly={previewOnly}
                          key={`${conditionalStep.template.id}-${conditionalStep.id}-${conditionalStep.template.title.length}`}
                          emailThreadButton={
                            isEmail ? emailThreadButton : undefined
                          }
                          subject={
                            isEmail
                              ? emailSubject
                              : conditionalStep.template?.subject
                          }
                          enableSubjectMutation={
                            isReplyThreadSubject === 'newThread'
                          }
                          template={conditionalStep.template}
                          stepId={conditionalStep.strategyStepId}
                          onSuccess={handleSuccessEditConditionalTemplate}
                          onSaveReturnTemplate={onSaveReturnTemplate}
                          isSequenceInstance={isSequenceInstance}
                          personalization={personalization}
                          prospectId={prospectId}
                          accountId={accountId}
                          uniqueIdentifierOfTemplate={conditionalStep.id}
                          isConditional={true}
                          strategyId={strategyId}
                        />
                        {canManageAbTesting && (
                          <>
                            <div className="horizontal-divider" />

                            <div
                              onClick={() => {
                                openConditionalTemplatePicker(
                                  conditionalStep.type,
                                  true,
                                );
                              }}
                              className={'ab-testing-wrapper'}
                            >
                              <PlusIcon />

                              <Typography fontSize={'14px'} fontWeight={500}>
                                Add A/B testing
                              </Typography>
                            </div>
                          </>
                        )}
                      </>
                    ) : null}
                  </>
                </div>
              ) : null}
            </div>
          </GuidedTourSpotlight>
        </div>
      </ConditionalSequenceStepWrapper>
    </>
  );
};

export default ConditionalStepDetailComponent;

export const RenderConditionalStepType = ({
  step,
  firstLinkedinMessageAfterInvitation,
  isSequenceInstance,
  style,
  handleTypeChange,
  previewOnly,
  openStrategySelectorModal,
  stopAndJumpAlreadyPresent,
}: {
  step: StrategyConditionalStep;
  firstLinkedinMessageAfterInvitation: boolean;
  isSequenceInstance: boolean;
  style?: React.CSSProperties;
  handleTypeChange: (
    newConditionalType: SwitchToTask,
    jumpToStrategyId?: number,
  ) => void;
  previewOnly?: boolean;
  openStrategySelectorModal?: () => void;
  stopAndJumpAlreadyPresent?: boolean;
}) => {
  const { hasSalesNav } = useAuth();

  const [openTypeTooltip, setOpenTypeTooltip] = useState<boolean>(false);
  const switchToAnotherTypeOptions = useMemo(() => {
    if (
      isSequenceInstance ||
      previewOnly ||
      // We don't allow to mutate other than conditional of stopAndJumpToAnotherStrategy if such conditional is present.
      // So in this case we only allow to change the one with stop and jump to another type, after which we release the block from other conditionals)
      (stopAndJumpAlreadyPresent &&
        step.type !== SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY)
    )
      return null;

    const options: SwitchTypeOption[] = [];
    if (step.type !== SwitchToTask.IN_MAIL && hasSalesNav) {
      options.push({
        type: SwitchToTask.IN_MAIL,
        icon: <LinkedinIconInMailCompass viewBox="0 2 20 20" />,
        text: 'Change to Linkedin InMail',
      });
    }

    if (step.type !== SwitchToTask.SKIP) {
      options.push({
        type: SwitchToTask.SKIP,
        icon: <SkipIcon color={colors.black} />,
        text: 'Skip & Jump to the next task',
      });
    }

    if (
      step.type !== SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY &&
      firstLinkedinMessageAfterInvitation &&
      openStrategySelectorModal
    ) {
      options.push({
        type: SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY,
        icon: <IconStrategySettings color={colors.black} size={18} />,
        text: 'Stop & Jump to another sequence',
      });
    }

    if (options.length === 0) {
      return null;
    }

    return options.map((option, indexOption) => {
      const separatorNeeded =
        options.length > 1 &&
        option.type === SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY;

      return (
        <React.Fragment key={indexOption}>
          {separatorNeeded && (
            <hr
              style={{
                border: 'none',
                borderTop: `1px solid ${colors.grey444}`,
                margin: '4px 0',
                width: '100%',
              }}
            />
          )}
          <div>
            <Button
              className="option-button"
              color="primary"
              fullWidth
              endIcon={option.icon}
              onClick={() => {
                if (
                  option.type ===
                    SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY &&
                  openStrategySelectorModal
                ) {
                  openStrategySelectorModal();
                } else {
                  handleTypeChange(option.type);
                }
                setOpenTypeTooltip(false);
              }}
            >
              {option.text}
            </Button>
          </div>
        </React.Fragment>
      );
    });
  }, [
    step,
    firstLinkedinMessageAfterInvitation,
    stopAndJumpAlreadyPresent,
    openStrategySelectorModal,
    isSequenceInstance,
    hasSalesNav,
    handleTypeChange,
    setOpenTypeTooltip,
  ]);

  let icon = null;
  let type = '';
  // We fake all other conditionals to be 'Stop & Jump to another sequence' if stopAndJumpAlreadyPresent
  // This is done in FE so if the user decide to change back to Skip or InMail all other conditionals are saved
  // as they were before user switched to 'Stop & Jump to another sequence'
  if (stopAndJumpAlreadyPresent) {
    icon = <CircularStopAndJumpToAnotherStrategyIcon />;
    type = 'Stop & Jump to another sequence';
  } else {
    switch (step.type) {
      case SwitchToTask.TO_CALL:
        icon = <CallIconDark />;
        type = 'Call';
        break;
      case SwitchToTask.TO_EMAIL:
        icon = <EmailIconDark />;
        type = 'Email';
        break;
      case SwitchToTask.SKIP:
        icon = <CloseIconDark />;
        type = 'Skip & Jump to the next task';
        break;
      case SwitchToTask.IN_MAIL:
        icon = <LinkedinIconInMailCompassDark />;
        type = 'InMail';
        break;
      case SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY:
        icon = <CircularStopAndJumpToAnotherStrategyIcon />;
        type = 'Stop & Jump to another sequence';
    }
  }

  const label = (
    <Typography fontSize={'12px'} fontWeight={500} paddingLeft={'12px'}>
      {type}
    </Typography>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
      }}
    >
      {icon}
      {switchToAnotherTypeOptions ? (
        <div
          className="step-type-option-dropdown"
          style={{
            ...style,
            width: step.type === SwitchToTask.IN_MAIL ? '100px' : '280px',
          }}
        >
          <ClickAwayListener onClickAway={() => setOpenTypeTooltip(false)}>
            <div style={{ width: '100%' }}>
              <CustomOptionsTooltip
                maxHeight={220}
                width={280}
                left={1}
                top={-2}
                title={
                  <StepTypeOptionsWrapper>
                    {switchToAnotherTypeOptions}
                  </StepTypeOptionsWrapper>
                }
                disableFocusListener
                disableHoverListener
                disableTouchListener
                open={openTypeTooltip}
                onClose={() => setOpenTypeTooltip(false)}
                borderRadius={'16px'}
                sx={{
                  zIndex: '10001 !important',
                }}
              >
                <div
                  className="step-type-option-container"
                  onClick={() => {
                    setOpenTypeTooltip((prev) => !prev);
                  }}
                >
                  <div className="step-type-option">{label}</div>
                  {openTypeTooltip ? <TopArrowIcon /> : <BottomArrowIcon />}
                </div>
              </CustomOptionsTooltip>
            </div>
          </ClickAwayListener>
        </div>
      ) : (
        label
      )}
    </div>
  );
};

const CircularStopAndJumpToAnotherStrategyIcon = () => {
  return (
    <FlexDiv
      style={{
        borderRadius: '50%',
        backgroundColor: colors.grey1,
      }}
      height={'32px'}
      width={'32px'}
    >
      <IconStrategySettings color={colors.white} size={18} />
    </FlexDiv>
  );
};
